/**
 * Quick filters — the default filter pane (spec M1-B4 §8.5,
 * `[ui:library/table-06-filter-pane.png]`).
 *
 * Rebuilt in round 5o from the sandbox capture (`specs/M1-B6-filters-sort.md` §4.2,
 * `discovery/ui-reference/filters/sandbox-quick-filter.png`): the pane is 800 × 443 at
 * (640, 237), an `All columns` heading over a horizontally scrolling strip of 150 px columns
 * on a **166 px pitch**, each a 20 px title row over `li` values **146 × 32 on a 38 px pitch**.
 *
 * Round 8c (parity card P-031) finished the chrome: the header is **one line** — title, the
 * `Showing …` count and an ⓘ — the values are grey **chips** with the count right-aligned
 * inside them, five are visible and the list scrolls, and a `›` pages the column strip.
 *
 * Four measured corrections to round 3:
 *  - the **`Name`** column is offered, first;
 *  - the empty bucket is labelled **`Blank`**, sits last, and is absent when nothing is blank
 *    (the server no longer sends it in that case);
 *  - the value order is the column's **declared vocabulary**, not the count — the server
 *    orders it, so this component renders `facets[columnId]` as it arrives;
 *  - `Clear all` clears to **nothing**, not to the saved view's filter.
 *
 * Clicking values inside one column is `any_of` over them; across columns the groups combine
 * with AND, which is exactly the tree `quickFilterToGroup` builds — so "Switch to advanced
 * filters" is lossless.
 */
import { Icon, icons } from '@elizmonday/ui';
import type { ColumnId } from '@elizmonday/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getColumnUi } from '../../columns/registry';
import { useTable, useTableActions } from '../context';
import { BLANK_FACET, groupToQuickFilter, quickFilterToGroup, type TableViewSettings } from '../settings';

import { Pane, PANE_BOX } from './Pane';
import { useSaveLabel } from './useSaveLabel';
import './quick-filter.css';

/**
 * **P-031, decided explicitly.** Round 5o read `Show more` into the pane; the parity shot says
 * monday shows **five values and scrolls** — the column box is 216 px tall over 32 px values on
 * a 38 px pitch (`filters/sandbox-quick-filter.json`). We take monday's: the server already
 * caps each column at 50 facets (`board-read.ts` `FACET_LIMIT`), so one scrolling list reaches
 * every value a `Show more` could have paged to, with no extra control and no extra state.
 *
 * How far one press of the `›` pager scrolls the column strip: one column, one 166 px pitch.
 */
const COLUMN_PITCH = 166;

export function QuickFilterPane({ onSwitchToAdvanced }: { onSwitchToAdvanced(): void }) {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const facets = useTable((state) => state.facets);
  const dirty = useTable((state) => state.dirty);
  const actions = useTableActions();
  const saveLabel = useSaveLabel();

  const selection = useMemo(() => groupToQuickFilter(effective.filter), [effective.filter]);
  const total = payload?.totals.items ?? 0;
  const filtered = payload?.totals.filtered ?? 0;
  const unfiltered = effective.filter === null && effective.search === '';

  // Measured: `Name` **is** offered, and it is the first column.
  const columns = (payload?.columns ?? []).filter((column) => (facets[column.id]?.length ?? 0) > 0);

  /*
   * P-031: the column strip is wider than the pane, and monday pages it with a `›` at the
   * right edge (x 1416 on the parity shot). The chevrons appear only when there is something
   * to scroll to, so a board with three columns shows none.
   */
  const strip = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const node = strip.current;
    if (node === null) return;
    setScroll({ left: node.scrollLeft > 1, right: node.scrollLeft + node.clientWidth < node.scrollWidth - 1 });
  }, []);
  useEffect(measure, [measure, columns.length]);
  const page = (direction: -1 | 1): void => {
    strip.current?.scrollBy({ left: direction * COLUMN_PITCH, behavior: 'smooth' });
  };

  const toggle = (columnId: ColumnId, value: unknown) => {
    // A filter the quick pane cannot express (a nested advanced tree) is replaced wholesale.
    const current = new Map(selection ?? new Map<ColumnId, unknown[]>());
    const existing = current.get(columnId) ?? [];
    const key = String(value);
    current.set(
      columnId,
      existing.some((entry) => String(entry) === key)
        ? existing.filter((entry) => String(entry) !== key)
        : [...existing, value],
    );
    actions.setEffective({ filter: quickFilterToGroup(current) } as Partial<TableViewSettings>);
  };

  return (
    <Pane
      testId="quick-filter-pane"
      title="Quick filters"
      meta={
        <>
          <span className="ez-quick-filter__count-line" data-testid="filter-count">
            {/* Measured verbatim, both cases. */}
            {unfiltered ? `Showing all of ${total} items` : `Showing ${filtered} of ${total} items`}
          </span>
          <span
            className="ez-quick-filter__info"
            data-testid="filter-info"
            title="Pick values to narrow the board. Values inside a column are OR; columns are AND."
          >
            <Icon icon={icons.info} size={16} ignoreFocusStyle />
          </span>
        </>
      }
      {...PANE_BOX.quickFilter}
      dirty={dirty}
      canSave={payload?.permissions.canSaveView ?? false}
      saveLabel={saveLabel}
      onSave={() => void actions.saveView()}
      // Measured: `Clear all` clears to nothing — the pane goes back to `Showing all of n`.
      onClear={() => actions.setEffective({ filter: null })}
      clearDisabled={effective.filter === null}
      onClose={() => actions.openPane(null)}
      footer={
        <button type="button" className="ez-board-pane__link" onClick={onSwitchToAdvanced}>
          Switch to advanced filters
        </button>
      }
    >
      {/* Measured: an `All columns` heading over the strip (`sandbox-quick-filter.json`). */}
      <h3 className="ez-quick-filter__heading" data-testid="filter-all-columns">
        All columns
      </h3>

      <div className="ez-quick-filter__strip">
        <div className="ez-quick-filter" ref={strip} onScroll={measure}>
          {selection === null ? (
            <p className="ez-quick-filter__complex">
              This view uses an advanced filter. Switch to advanced filters to edit it.
            </p>
          ) : (
            columns.map((column) => {
              const picked = (selection.get(column.id) ?? []).map(String);
              const ui = getColumnUi(column.type);
              const values = facets[column.id] ?? [];
              return (
                <section key={column.id} className="ez-quick-filter__column" data-testid={`facet-${column.id}`}>
                  {/* Measured: the heading carries the same `/ n` badge as the toolbar. */}
                  <h3 className="ez-quick-filter__title">
                    {picked.length > 0 ? `${column.title} / ${String(picked.length)}` : column.title}
                  </h3>
                  <ul className="ez-quick-filter__values">
                    {values.map((facet) => {
                      const value = facet.value === BLANK_FACET ? BLANK_FACET : facet.value;
                      const on = picked.includes(String(value));
                      return (
                        <li key={String(facet.value)}>
                          <button
                            type="button"
                            className="ez-quick-filter__value"
                            aria-pressed={on}
                            data-align={ui.align}
                            onClick={() => toggle(column.id, value)}
                          >
                            <span className="ez-quick-filter__label">{facet.label}</span>
                            <span className="ez-quick-filter__count">{facet.count}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })
          )}
        </div>
        {scroll.left ? (
          <button
            type="button"
            className="ez-quick-filter__pager ez-quick-filter__pager--left"
            aria-label="Show the previous columns"
            data-testid="filter-pager-left"
            onClick={() => page(-1)}
          >
            <Icon icon={icons.chevronLeft} size={16} ignoreFocusStyle />
          </button>
        ) : null}
        {scroll.right ? (
          <button
            type="button"
            className="ez-quick-filter__pager ez-quick-filter__pager--right"
            aria-label="Show the next columns"
            data-testid="filter-pager-right"
            onClick={() => page(1)}
          >
            <Icon icon={icons.chevronRight} size={16} ignoreFocusStyle />
          </button>
        ) : null}
      </div>
    </Pane>
  );
}
